// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useQuery } from '@apollo/client';
import type { SelectChangeEvent } from '@mui/material/Select';
import { ORDER_STATUS } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router';
import { TitleCard } from '../components/common/card';
import EmptyContainer from '../components/common/empty/EmptyContainer';
import { PageError } from '../components/common/error';
import {
  ALL_ITEMS,
  getNewSelectedValues,
  makeDatePickerFilter,
  makeMultiSelectFilter,
} from '../components/common/filter';
import { PageLoading } from '../components/common/loading';
import PagingView from '../components/common/view/PagingView';
import AddOrderDialog from '../components/order/AddOrderDialog';
import OrderTable from '../components/order/OrderTable';
import { useGating, useUserContext } from '../context/UserContext';
import { Box, Button, Typography } from '../lib/base';
import { AddIcon } from '../lib/icons';
import { CUSTOMERS_QUERY } from '../queries/CustomersQuery';
import { ORDERS_QUERY } from '../queries/OrdersQueries';
import type {
  CustomersQuery,
  CustomersQueryVariables,
  OrdersHomeQuery,
  OrdersHomeQueryVariables,
} from '../types/__generated__/graphql';
import { getPagingVariables, getTimeRanges } from '../utils';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const PARAMS = {
  status: 'status',
  customer: 'customer',
  timeAfter: 'start',
  timeBefore: 'end',
  page: 'page',
  pageSize: 'pageSize',
};
const defaultStatusFilter = [ORDER_STATUS.PLANNED, ORDER_STATUS.IN_PROGRESS];

export default React.memo(function Orders() {
  const { permissions, timezone } = useUserContext();
  const { canUseBasic } = useGating();
  const { todayStartDate } = getTimeRanges(timezone);
  const [searchParams, setSearchParams] = useSearchParams();

  const [newOrderOpen, setNewOrderOpen] = React.useState(false);
  const [nameFilter, setNameFilter] = React.useState<string[]>(
    searchParams.get(PARAMS.customer)?.split(',') ?? [ALL_ITEMS],
  );
  const [statusFilter, setStatusFilter] = React.useState<string[]>(
    searchParams.get(PARAMS.status)?.split(',') ?? defaultStatusFilter,
  );
  const [startDateFilter, setStartDateFilter] = React.useState<dayjs.Dayjs | null>(() => {
    const paramsIn = searchParams.get(PARAMS.timeAfter);
    if (paramsIn == null || Number.isNaN(Number(paramsIn))) {
      return todayStartDate;
    }
    const timeAfterNum = Number.parseInt(paramsIn) || 0;
    if (timeAfterNum <= 0) {
      return todayStartDate;
    }

    const date = dayjs.unix(timeAfterNum).tz(timezone);

    return date.isValid() ? date : todayStartDate;
  });
  const [endDateFilter, setEndDateFilter] = React.useState<dayjs.Dayjs | null>(() => {
    const paramsIn = searchParams.get(PARAMS.timeBefore);
    if (paramsIn == null || Number.isNaN(Number(paramsIn))) {
      return null;
    }
    const timeBeforeNum = Number.parseInt(paramsIn) || 0;
    if (timeBeforeNum <= 0) {
      return null;
    }

    const date = dayjs.unix(timeBeforeNum).tz(timezone);

    return date.isValid() ? date : null;
  });
  const [currentPage, setCurrentPage] = React.useState(Number.parseInt(searchParams.get(PARAMS.page) || '1'));
  const [pageSize, setPageSize] = React.useState(Number.parseInt(searchParams.get(PARAMS.pageSize) || '25'));

  const { data: customersData } = useQuery<CustomersQuery, CustomersQueryVariables>(CUSTOMERS_QUERY);
  const { data, loading, refetch, error } = useQuery<OrdersHomeQuery, OrdersHomeQueryVariables>(ORDERS_QUERY, {
    variables: {
      ...getPagingVariables(pageSize, currentPage),
      status: statusFilter.includes(ALL_ITEMS) || statusFilter.length === 0 ? undefined : statusFilter.join(','),
      customer: nameFilter.includes(ALL_ITEMS) || nameFilter.length === 0 ? undefined : nameFilter.join(','),
      timeAfter: startDateFilter?.unix()?.toString() ?? undefined,
      timeBefore: endDateFilter?.unix()?.toString() ?? undefined,
    },
  });

  // Update URL params whenever state changes
  React.useEffect(() => {
    const currentParams = {
      [PARAMS.status]: searchParams.get(PARAMS.status) ?? undefined,
      [PARAMS.customer]: searchParams.get(PARAMS.customer) ?? undefined,
      [PARAMS.timeAfter]: searchParams.get(PARAMS.timeAfter) ?? undefined,
      [PARAMS.timeBefore]: searchParams.get(PARAMS.timeBefore) ?? undefined,
      [PARAMS.page]: searchParams.get(PARAMS.page) ?? undefined,
    };
    const params = {
      [PARAMS.status]: statusFilter.includes(ALL_ITEMS) ? undefined : statusFilter.join(','),
      [PARAMS.customer]: nameFilter.includes(ALL_ITEMS) ? undefined : nameFilter.join(','),
      [PARAMS.timeAfter]: startDateFilter ? startDateFilter.unix().toString() : undefined,
      [PARAMS.timeBefore]: endDateFilter ? endDateFilter.unix().toString() : undefined,
      [PARAMS.page]: currentPage.toString(),
    };

    // Check if any of the parameters have changed
    // This is to preserve navigation history
    // When navigating to a new page from orders, then back to orders,
    // if setSearchParams() is called, then it clears the forward nav history
    const hasParamsChanged = Object.entries(params).some(([key, value]) => value !== currentParams[key]);

    if (hasParamsChanged) {
      // Filter out undefined params
      const newSearchParams = new URLSearchParams();
      const entries = Object.entries(params);
      for (const [key, value] of entries) {
        if (value !== undefined) {
          newSearchParams.set(key, value);
        }
      }

      setSearchParams(newSearchParams);
    }
  }, [statusFilter, nameFilter, startDateFilter, endDateFilter, currentPage, setSearchParams, searchParams]);

  const onChangeStatusFilter = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...statusFilter];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);

      setStatusFilter(newSelectedValues);
      setCurrentPage(1);
      const pagingArgs = getPagingVariables(pageSize, 1);
      void refetch({
        ...pagingArgs,
        status:
          newSelectedValues.length === 0 || newSelectedValues.includes(ALL_ITEMS)
            ? undefined
            : newSelectedValues.join(','),
      });
    },
    [refetch, statusFilter, pageSize],
  );
  const statusFilterMenu = React.useMemo(
    () =>
      makeMultiSelectFilter({
        value: statusFilter,
        onChangeValue: onChangeStatusFilter,
        label: 'Status Filter',
        id: 'status-filter',
        options: [
          { value: ORDER_STATUS.PLANNED, label: 'Planned' },
          { value: ORDER_STATUS.IN_PROGRESS, label: 'In Progress' },
          { value: ORDER_STATUS.COMPLETED, label: 'Completed' },
          { value: ORDER_STATUS.CANCELLED, label: 'Cancelled' },
        ],
      }),
    [onChangeStatusFilter, statusFilter],
  );
  const onChangeName = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...nameFilter];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setNameFilter(newSelectedValues);
      setCurrentPage(1);
      const pagingArgs = getPagingVariables(pageSize, 1);
      void refetch({
        ...pagingArgs,
        customer:
          newSelectedValues.length === 0 || newSelectedValues.includes(ALL_ITEMS)
            ? undefined
            : newSelectedValues.join(','),
      });
    },
    [refetch, nameFilter, pageSize],
  );
  const currentNameFilter = React.useMemo(
    () =>
      makeMultiSelectFilter({
        value: nameFilter,
        onChangeValue: onChangeName,
        label: 'Customer Filter',
        id: 'customer-filter',
        options: customersData?.customers?.map((customer) => ({ value: customer.name, label: customer.name })) ?? [],
      }),
    [customersData, nameFilter, onChangeName],
  );

  const onChangeStartDate = React.useCallback(
    (newVal: dayjs.Dayjs | null) => {
      const newDateInTz = newVal?.tz(timezone, true).startOf('day') ?? null;
      setStartDateFilter(newDateInTz);
      setCurrentPage(1);
      const pagingArgs = getPagingVariables(pageSize, 1);
      void refetch({
        ...pagingArgs,
        timeAfter: newDateInTz?.unix().toString() ?? undefined,
      });
    },
    [refetch, timezone, pageSize],
  );
  const onChangeEndDate = React.useCallback(
    (newVal: dayjs.Dayjs | null) => {
      const newDateInTz = newVal?.tz(timezone, true).endOf('day') ?? null;
      setEndDateFilter(newDateInTz);
      setCurrentPage(1);
      const pagingArgs = getPagingVariables(pageSize, 1);
      void refetch({
        ...pagingArgs,
        timeBefore: newDateInTz?.unix().toString() ?? undefined,
      });
    },
    [refetch, timezone, pageSize],
  );
  const startDateFilterMenu = React.useMemo(
    () =>
      makeDatePickerFilter({
        value: startDateFilter,
        onChangeValue: onChangeStartDate,
        maxDate: endDateFilter ?? undefined,
        label: 'After',
      }),
    [startDateFilter, onChangeStartDate, endDateFilter],
  );
  const endDateFilterMenu = React.useMemo(
    () =>
      makeDatePickerFilter({
        value: endDateFilter,
        onChangeValue: onChangeEndDate,
        minDate: startDateFilter ?? undefined,
        label: 'Before',
      }),
    [endDateFilter, onChangeEndDate, startDateFilter],
  );

  const dateRange = React.useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          alignItems: { xs: 'flex-start', sm: 'center' },
        }}
      >
        {startDateFilterMenu}
        <Box sx={{ display: { xs: 'none', sm: 'inline-block' } }}>
          <span>-</span>
        </Box>
        {endDateFilterMenu}
      </Box>
    ),
    [startDateFilterMenu, endDateFilterMenu],
  );
  const currentOrdersFiltersRow = React.useMemo(
    () => (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          alignItems: { xs: 'flex-start', sm: 'center' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            alignItems: { xs: 'flex-start', sm: 'center' },
          }}
        >
          {currentNameFilter}
          {statusFilterMenu}
        </Box>
        {dateRange}
      </Box>
    ),
    [currentNameFilter, statusFilterMenu, dateRange],
  );

  const userCanEditOrders = React.useMemo(() => permissions.can_edit_orders, [permissions]);

  const content = React.useMemo(() => {
    if (error) {
      return <PageError error={error} />;
    }
    if (loading) {
      return <PageLoading />;
    }
    const orders = data?.orders?.edges.map(({ node }) => node) ?? [];
    if (!loading && orders.length === 0) {
      return (
        <EmptyContainer
          title="No orders found!"
          description={'Click the "New Order +" button to start creating orders.'}
        />
      );
    }
    const hasPreviousPage = currentPage > 1;
    const hasNextPage = data?.orders?.pageInfo.hasNextPage ?? false;

    return (
      <PagingView
        content={<OrderTable orders={orders} showOptions />}
        isInitialLoad={loading}
        // Could be better used if there is existing data, but more data is being fetched
        isLoadingMore={loading}
        page={currentPage}
        pageSize={pageSize}
        currentPageSize={orders.length}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onUpdatePage={(pageNum: number) => {
          setCurrentPage(pageNum);
          const newArgs = getPagingVariables(pageSize, pageNum);
          void refetch({ ...newArgs });
        }}
        onUpdatePageSize={(pageSize) => {
          setCurrentPage(1);
          setPageSize(pageSize);
          const newArgs = getPagingVariables(pageSize, 1);
          void refetch({ ...newArgs });
        }}
        error={error}
        omitPager={false}
        disablePadding={false}
      />
    );
  }, [loading, error, data, refetch, currentPage, pageSize]);

  const options = React.useMemo(
    () =>
      userCanEditOrders ? (
        <Button
          id="new-order-button"
          variant="contained"
          onClick={() => setNewOrderOpen(true)}
          endIcon={<AddIcon sx={{ color: 'secondary.contrastText' }} />}
          color="secondary"
          disabled={!canUseBasic}
        >
          <Typography variant="body1" sx={{ color: 'secondary.contrastText' }}>
            New Order
          </Typography>
        </Button>
      ) : (
        <></>
      ),
    [userCanEditOrders, canUseBasic],
  );

  const handleNewOrderClose = React.useCallback(() => {
    setNewOrderOpen(false);
  }, []);
  const handleNewOrderSuccess = React.useCallback(() => {
    void refetch();
    setNewOrderOpen(false);
  }, [refetch]);

  const titleCard = React.useMemo(() => {
    return (
      <TitleCard title="Orders" rightElement={options} content={content} extraHeaderContent={currentOrdersFiltersRow} />
    );
  }, [options, content, currentOrdersFiltersRow]);

  const addOrderDialog = React.useMemo(() => {
    return (
      <AddOrderDialog
        isForNewSingleOrder={true}
        isOpen={newOrderOpen}
        onClose={handleNewOrderClose}
        onSuccess={handleNewOrderSuccess}
      />
    );
  }, [newOrderOpen, handleNewOrderClose, handleNewOrderSuccess]);

  return (
    <>
      <Helmet>
        <title>{'Orders'}</title>
      </Helmet>
      {titleCard}
      {newOrderOpen && addOrderDialog}
    </>
  );
});
